import { PRIVILEGES } from "@/apis/privileges/constants";

function checkPrivilege(privileges, permission) {
  return (privileges || []).includes(permission);
}

export function createHostingPrivilegeMethods() {
  function hasPrivilege(permission) {
    return checkPrivilege(this.privileges, permission);
  }

  return {
    hasPrivilege,
    hasAnyHostingPrivilege() {
      return (this.privileges || []).some((str) => str.startsWith("hosting."));
    },
    hasManageEmailPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_EMAIL);
    },
    hasManageDnsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_DNS);
    },
    hasManageDomainsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_DOMAINS);
    },
    hasManageSslPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_SSL);
    },
    hasManageFtpPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_FTP);
    },
    hasManageSftpPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_SFTP);
    },
    hasManageMysqlPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_MYSQL);
    },
    hasManageCronPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_CRON);
    },
    hasManageFileManagerPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_FILE_MANAGER);
    },
    hasControlPanelSsoPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.CONTROL_PANEL_SSO);
    },
    hasPhpmyadminSsoPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.PHPMYADMIN_SSO);
    },
    hasChangePhpVersionPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.CHANGE_PHP_VERSION);
    },
    hasManagePhpSettingsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_PHP_SETTINGS);
    },
  };
}

export function createInstancePrivilegeMethods(type) {
  function hasPrivilege(permission) {
    return checkPrivilege(this.privileges, permission);
  }

  return {
    hasPrivilege,

    // GENERAL
    hasManageLabelsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_LABELS);
    },
    hasRefreshThumbnailPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.REFRESH_THUMBNAIL);
    },
    hasChangeDomainPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.CHANGE_DOMAIN);
    },
    hasCreateStagingPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.CREATE_STAGING);
    },
    hasPushToLivePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.PUSH_TO_LIVE);
    },
    hasPushToStagingPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.PUSH_TO_STAGING);
    },
    hasViewLogsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.VIEW_LOGS);
    },
    hasViewPhpLogsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.VIEW_PHP_LOGS);
    },
    hasViewWebserverLogsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.VIEW_WEBSERVER_LOGS);
    },
    hasViewReportsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.VIEW_REPORTS);
    },
    hasManageCdnPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_CDN);
    },
    hasDeleteInstancePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.DELETE_INSTANCE);
    },
    hasCopyInstancePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.COPY_INSTANCE);
    },
    hasManageSharedAccessPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_SHARED_ACCESS);
    },
    hasPreviewInstancePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.PREVIEW_INSTANCE);
    },
    hasCollaborationPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.COLLABORATION);
    },

    // WORDPRESS
    hasInstallationDetails() {
      return type === "wordpress";
    },
    hasViewInstallationDetailsPrivilege() {
      return (
        type === "wordpress" &&
        checkPrivilege(this.privileges, PRIVILEGES.VIEW_INSTALLATION_DETAILS)
      );
    },
    hasCreateSsoLinkPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.CREATE_SSO_LINK);
    },
    hasWordPressUpdatePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.WORDPRESS_UPDATE);
    },
    hasManageAutoUpdatePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_AUTO_UPDATE);
    },
    hasManageBackupsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_BACKUPS);
    },
    hasManageAutoBackupPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_AUTO_BACKUP);
    },
    hasUpdateSiteNamePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.UPDATE_SITE_NAME);
    },
    hasClearCachePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.CLEAR_CACHE);
    },
    hasManageMaintenanceModePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_MAINTENANCE_MODE);
    },
    hasManageDebugModePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_DEBUG_MODE);
    },
    hasManageThemesPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_THEMES);
    },
    hasManagePluginsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_PLUGINS);
    },
    hasManageConfigPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_CONFIG);
    },
    hasManageUsersPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_USERS);
    },
    hasManageEdgeCachePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_EDGE_CACHE);
    },
    hasManageSearchEngineIndexingPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_SEARCH_ENGINE_INDEXING);
    },
    hasWordPressViewLogsPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.WORDPRESS_VIEW_LOGS);
    },
    hasManageCloudflarePrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_CLOUDFLARE);
    },
    hasManageMcpPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_MCP);
    },
    hasManageGitPrivilege() {
      return checkPrivilege(this.privileges, PRIVILEGES.MANAGE_GIT);
    },

    ...createHostingPrivilegeMethods(),
  };
}
